// react
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES, DYNAMIC_ROUTES } from 'router';

// components
import { CareCirclePage } from '@karehero/llama/pwa/pages/CareCirclePage/CareCirclePage';

// hooks
import { useCareCircle } from 'hooks';

// utils
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';
import { reportInviteError } from 'utils/inviteError';

// store
import { dispatch, useSelector } from 'store';
import { useGetCareCircleInvitesQuery } from 'store/api/careCircle';
import {
  useDeleteInviteMutation,
  useReinviteMemberMutation,
} from 'store/api/organization';
import { newToast } from 'store/slices/uiSlice';
import { getAccount } from 'store/slices/iamSlice';

/**
 * CareCircle is the care circle page.
 */
export const CareCircle = () => {
  // hooks
  const { careCircleId, careCircle, careRecipientFirstName } = useCareCircle();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const account = useSelector(getAccount);
  const { data: invites } = useGetCareCircleInvitesQuery(careCircleId!, {
    skip: !careCircleId,
  });
  const [reinviteMember] = useReinviteMemberMutation();
  const [deleteInvite] = useDeleteInviteMutation();

  // methods
  const handleResendInvite = useCallback(
    async (inviteId: string) => {
      try {
        await reinviteMember(inviteId).unwrap();
        dispatch(newToast({ title: 'Invite resent', variant: 'success' }));
      } catch (e) {
        reportInviteError(e);
      }
    },
    [reinviteMember],
  );

  const handleRemoveInvite = useCallback(
    async (inviteId: string) => {
      try {
        await deleteInvite(inviteId).unwrap();
        dispatch(newToast({ title: 'Invite removed', variant: 'success' }));
      } catch (e) {
        reportInviteError(e);
      }
    },
    [deleteInvite],
  );

  // memos
  const registeredMembers = useMemo(() => {
    if (!careCircle?.careCircleMembers) return [];
    return careCircle.careCircleMembers.map((member) => {
      const isCareRecipient = member.roles?.some(
        (r) => r.name === 'Care Recipient',
      );
      const role = isCareRecipient
        ? 'Care Recipient'
        : member.careRecipientAccountRelationship?.name ||
          member.roles?.[0]?.name ||
          'Member';
      return {
        id: member.id,
        name: `${member.account?.firstName || ''} ${member.account?.lastName || ''}`.trim(),
        role,
        email: isCareRecipient ? undefined : member.account?.email,
        avatarSrc: getProfilePictureUrl(member.account),
        onView: () => navigate(DYNAMIC_ROUTES.CARE_CIRCLE_MEMBER(member.id)),
      };
    });
  }, [careCircle, navigate]);

  const invitedMembers = useMemo(() => {
    if (!invites) return [];
    return invites.map((invite) => ({
      id: invite.id,
      name: invite.firstName || invite.email || 'Invited',
      role: 'Invited',
      email: invite.email,
      onResendInvite: () => handleResendInvite(invite.id),
      onRemove: () => handleRemoveInvite(invite.id),
    }));
  }, [invites, handleResendInvite, handleRemoveInvite]);

  const members = useMemo(
    () => [...registeredMembers, ...invitedMembers],
    [registeredMembers, invitedMembers],
  );

  const isRecipientUser =
    !!account?.id && account.id === careCircle?.careRecipientAccount?.id;

  return (
    <CareCirclePage
      members={members}
      recipientFirstName={careRecipientFirstName}
      isRecipientUser={isRecipientUser}
      onInvite={() => navigate(ROUTES.CARE_CIRCLE_INVITE)}
      onBack={goBack}
    />
  );
};

export default CareCircle;
